import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Sparkles, Scale, FileText, FileSearch, Gavel, Briefcase, Shield } from "lucide-react";

const PACKETS = [
  { id: "grievance", title: "Auto Grievance Generator", price: "$9.99", icon: Shield, route: "/intake/grievance", live: true,
    bullets: ["Formal DOCCS Directive #4040 format", "6-question intake", "Instant PDF delivery"],
    desc: "Generate a clean, properly-formatted facility grievance in minutes." },
  { id: "hearing", title: "Hearing Preparation Intake", price: "Free review", icon: Scale, route: "/intake/hearing", live: true, free: true,
    bullets: ["Tier 2 / Tier 3 / parole / court hearings", "Submitted to admin for review", "Personalized prep plan response"],
    desc: "Tell us about an upcoming hearing. Our team reviews and replies with a structured prep plan." },
  { id: "parole", title: "Parole Packet", price: "$99.99", icon: Gavel, route: "/intake/parole", live: true,
    bullets: ["Cover page · cover letter", "Personal statement (5 structured subsections)", "Reentry plan · rehab summary · exhibit index"],
    desc: "A complete, NYS-aligned Parole Board submission packet." },
  { id: "divorce", title: "Divorce (NY) — Overview", price: "Consultation", icon: FileText, route: "/divorce-overview", live: true, free: true,
    bullets: ["Plain-English orientation", "Schedule a consultation", "Personalized referral guidance"],
    desc: "Uncontested divorce in NY is rarely a one-size form. Read the overview, then talk to us." },
  { id: "immigration", title: "Immigration Assistance", price: "$99.99", icon: FileSearch, route: "/services/coming-soon?svc=immigration", live: false,
    bullets: ["Case summary", "Personal statement", "Document checklist & filing guide"],
    desc: "Organized case summary and supporting materials for immigration matters." },
];

const TIER2 = {
  title: "Tier 2 — Document Enhancement & Argument Preparation",
  desc: "Upgrade any packet with professional refinement: narrative restructuring, formatting polish, case organization.",
  bullets: ["Document refinement", "Narrative structuring", "Professional formatting", "Case organization"],
  cta: "Request a Tier 2 review",
};

function getServiceCta(s) {
  if (!s.live) return "Notify me";
  if (s.id === "divorce") return "Read overview";
  if (s.free) return "Start free intake";
  return "Start intake";
}

function ServiceCard({ s }) {
  const Icon = s.icon;
  return (
    <Link to={s.live ? s.route : s.route} className="group block border border-zinc-800 hover:border-amber-400/60 bg-zinc-950/60 hover:bg-blue-950/20 transition-colors rounded-sm p-6" data-testid={`svc-card-${s.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 h-11 w-11 rounded-sm border border-amber-400/40 bg-blue-950/30 flex items-center justify-center">
          <Icon className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="display-serif text-xl text-zinc-50 group-hover:text-amber-300 transition-colors">{s.title}</h3>
            <span className="font-mono text-sm text-amber-400 whitespace-nowrap">{s.price}</span>
          </div>
          <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{s.desc}</p>
          {s.bullets && (
            <ul className="mt-3 space-y-1">
              {s.bullets.map((b) => <li key={b} className="text-xs text-zinc-500 flex items-start gap-2"><span className="text-amber-400 mt-1">•</span>{b}</li>)}
            </ul>
          )}
          <div className="flex items-center justify-between mt-4">
            {!s.live && <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 border border-zinc-800 rounded-sm px-2 py-0.5">Coming soon</span>}
            <span className="text-amber-400 text-sm ml-auto group-hover:translate-x-1 transition-transform flex items-center gap-1">
              {getServiceCta(s)} <ArrowRight className="h-3.5 w-3.5"/>
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Services() {
  return (
    <div className="bg-black text-zinc-100">
      {/* Hero */}
      <section className="pt-16 pb-12 border-b border-zinc-900">
        <div className="container-pl">
          <p className="overline mb-4">Our services</p>
          <h1 className="display-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 leading-[1.05] mb-4 accent-rule">
            A complete legal support system.
          </h1>
          <p className="text-lg text-zinc-300 leading-relaxed max-w-2xl">
            Automated document packets for the cases that matter most — paired with premium support for when the stakes go higher.
          </p>
          <p className="text-[11px] text-zinc-500 italic mt-6 max-w-2xl">
            We are not attorneys and do not provide legal advice. All services are administrative and document preparation support only.
          </p>
        </div>
      </section>

      {/* Document packets */}
      <section className="py-14">
        <div className="container-pl">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="display-serif text-2xl sm:text-3xl text-zinc-50 accent-rule">Document Packets</h2>
            <p className="text-xs text-zinc-500 hidden sm:block">Self-service intake · instant PDF delivery</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {PACKETS.map((s) => <ServiceCard key={s.id} s={s}/>)}
          </div>
        </div>
      </section>

      {/* Tier 2 */}
      <section className="py-14 border-t border-zinc-900">
        <div className="container-pl">
          <div className="border border-amber-400/30 bg-gradient-to-br from-blue-950/30 to-black/40 rounded-sm p-8 lg:p-10" data-testid="tier2-panel">
            <div className="flex items-start gap-3 mb-3">
              <Sparkles className="h-5 w-5 text-amber-400" strokeWidth={1.5} />
              <p className="overline">Premium upgrade</p>
            </div>
            <h2 className="display-serif text-2xl sm:text-3xl text-zinc-50 mb-3">{TIER2.title}</h2>
            <p className="text-sm text-zinc-300 leading-relaxed max-w-2xl mb-5">{TIER2.desc}</p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-6">
              {TIER2.bullets.map((b) => <li key={b} className="text-sm text-zinc-300 flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-amber-400"/>{b}</li>)}
            </ul>
            <p className="text-[11px] text-zinc-500 italic mb-5">
              Administrative and document support only — never legal advice.
            </p>
            <Link to="/booking">
              <Button size="lg" variant="outline" className="rounded-sm border-amber-400 text-amber-400 hover:bg-blue-950/30" data-testid="tier2-cta">
                {TIER2.cta} <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* B2B teaser */}
      <section className="py-14 border-t border-zinc-900 bg-zinc-950/40">
        <div className="container-pl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="h-4 w-4 text-amber-400" strokeWidth={1.5} />
              <p className="overline">For attorneys & professionals</p>
            </div>
            <h2 className="display-serif text-2xl sm:text-3xl text-zinc-50">Outsource document preparation. Train your team.</h2>
          </div>
          <Link to="/attorneys"><Button className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white" data-testid="b2b-link">Explore B2B <ArrowRight className="ml-2 h-4 w-4"/></Button></Link>
        </div>
      </section>
    </div>
  );
}
